import React, { useState, useEffect } from "react";
import {
  Button,
  Checkbox,
  theme,
  Space,
  ConfigProvider,
  Tooltip,
  Segmented,
  Upload,
  Tag,
  message,
  Avatar,
} from "antd";
import {
  SendOutlined,
  LockOutlined,
  FontColorsOutlined,
  PaperClipOutlined,
  MessageOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

function CustomMentionPlugin(editor: any) {
  editor.model.schema.register("customMention", {
    isInline: true,
    isObject: true,
    allowWhere: "$text",
    allowAttributes: ["mentionName", "mentionId"],
  });

  editor.conversion.for("downcast").elementToElement({
    model: "customMention",
    view: (modelItem: any, { writer }: any) => {
      const name = modelItem.getAttribute("mentionName");
      const id = modelItem.getAttribute("mentionId");
      return writer.createRawElement(
        "span",
        {
          class: "mention-badge",
          "data-mention": id,
        },
        (domElement: HTMLElement) => {
          domElement.innerText = `@${name}`;
        }
      );
    },
  });

  editor.conversion.for("upcast").elementToElement({
    view: {
      name: "span",
      classes: "mention-badge",
    },
    model: (viewElement: any, { writer }: any) => {
      const name = viewElement.getAttribute("data-mention");
      return writer.createElement("customMention", { mentionName: name });
    },
  });
}

interface TicketReplyProps {
  isLinked?: boolean;
  reply: string;
  onChangeReply: (value: string) => void;
  isPrivate: boolean;
  onChangePrivate: (checked: boolean) => void;
  submitting: boolean;
  onSendReply: (isLinked?: boolean, attachments?: any[]) => Promise<boolean>;
  onCancel?: () => void;
  mentionOptions?: Array<{ label: string; value: string }>;
}

export const TicketReply: React.FC<TicketReplyProps> = ({
  reply,
  onChangeReply,
  isLinked,
  isPrivate,
  onChangePrivate,
  submitting,
  onSendReply,
  onCancel,
  mentionOptions = [],
}) => {
  const { token } = theme.useToken();
  const [isFocused, setIsFocused] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const [updateLinkedTickets, setUpdateLinkedTickets] = useState(false);
  const [editorInstance, setEditorInstance] = useState<any>(null);
  const [fileList, setFileList] = useState<any[]>([]);
  const limitMessageShown = React.useRef(false);
  const [mentionState, setMentionState] = useState({
    active: false,
    query: "",
    position: { bottom: 0, left: 0 },
    selectedIndex: 0,
  });
  const mentionStateRef = React.useRef(mentionState);
  const mentionOptionsRef = React.useRef(mentionOptions);
  
  useEffect(() => {
    mentionStateRef.current = mentionState;
    mentionOptionsRef.current = mentionOptions;
  }, [mentionState, mentionOptions]);
  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

  const handleBeforeUpload = (file: any) => {
    if (file.size > MAX_FILE_SIZE) {
      message.error(`Attachment "${file.name}" exceeds the maximum allowed size of 25 MB.`);
      return Upload.LIST_IGNORE;
    }
    
    setFileList((prev) => {
      if (prev.length >= 5) {
        if (!limitMessageShown.current) {
          message.info("Maximum 5 attachments allowed.");
          limitMessageShown.current = true;
          setTimeout(() => { limitMessageShown.current = false; }, 500);
        }
        return prev;
      }
      return [...prev, { name: file.name, file }];
    });
    
    return false;
  };

  const handleRemoveFile = (index: number) => {
    setFileList((prev) => prev.filter((_, i) => i !== index));
  };

  const subtleBorder = `1px solid ${token.colorBorderSecondary}`;
  const activeColor = isPrivate ? token.colorWarning : token.colorPrimary;
  const activeOutline = isPrivate
    ? `0 0 0 2px ${token.colorWarningOutline || "rgba(217, 119, 6, 0.12)"}`
    : `0 0 0 2px ${token.controlOutline || "rgba(0, 120, 212, 0.12)"}`;

  const composerBg = isPrivate
    ? "rgba(255, 197, 61, 0.03)"
    : token.colorBgContainer;

  const getPlaceholder = (privateMode: boolean) => {
    return privateMode
      ? "Write a private note (visible to agents only) ..."
      : "Write a reply in thread...";
  };

  // Dynamically update placeholder attribute on CKEditor's root DOM node
  useEffect(() => {
    const text = getPlaceholder(isPrivate);
    if (editorInstance) {
      editorInstance.config.set("placeholder", text);
    }
    const el = document.querySelector(".ticket-reply-editor-wrapper .ck-editor__editable");
    if (el) {
      el.setAttribute("data-placeholder", text);
    }
  }, [isPrivate, editorInstance]);

  // Clean empty HTML tag check to disable send button when empty
  const isReplyEmpty = (html: string) => {
    if (!html) return true;
    if (/<img|<table|<hr|<iframe/i.test(html)) return false;
    const clean = html.replace(/<\/?[^>]+(>|$)/g, "").replace(/&nbsp;/g, "").trim();
    return clean.length === 0;
  };

  const hasContent = !isReplyEmpty(reply) || fileList.length > 0;

  // Cancel and clear typed text and attachments
  const handleCancel = () => {
    if (editorInstance) {
      editorInstance.setData("");
    }
    onChangeReply("");
    setFileList([]);
    setIsFocused(false);
    setMentionState({ active: false, query: "", position: { bottom: 0, left: 0 }, selectedIndex: 0 });
    if (onCancel) {
      onCancel();
    }
  };

  const handleInsertMention = (user: { label: string; value: string }) => {
    if (!editorInstance) return;
    editorInstance.model.change((writer: any) => {
      const model = editorInstance.model;
      const selection = model.document.selection;
      const position = selection.getFirstPosition();
      if (!position) return;

      const mentionTextLength = mentionState.query.length + 1;
      const startPosition = model.createPositionAt(position.parent, Math.max(0, position.offset - mentionTextLength));

      const range = model.createRange(startPosition, position);
      writer.remove(range);

      const mentionElement = writer.createElement("customMention", { mentionName: user.label, mentionId: user.value });
      writer.insert(mentionElement, startPosition);

      const posAfter = model.createPositionAfter(mentionElement);
      writer.insertText(" ", posAfter);
      
      writer.setSelection(model.createPositionAt(posAfter.parent, posAfter.offset + 1));
    });
    setMentionState((prev) => ({ ...prev, active: false }));
    editorInstance.editing.view.focus();
  };

  const handleInsertMentionRef = React.useRef<any>(null);
  useEffect(() => {
    handleInsertMentionRef.current = handleInsertMention;
  }, [handleInsertMention]);

  return (
    <div
      style={{
        padding: "10px 16px",
        background: token.colorBgContainer,
        borderTop: subtleBorder,
      }}
    >
      <style>{`
        .send-icon-btn {
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .send-icon-btn:hover {
          transform: translateY(-1px) scale(1.02);
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08) !important;
        }
        .send-icon-btn:active {
          transform: translateY(0px) scale(0.98);
        }

        /* Hide powered-by CKEditor logo/link/balloon watermark */
        .ck-powered-by,
        .ck-powered-by-balloon,
        .ck.ck-powered-by-balloon,
        .ck.ck-link_selected.ck-powered-by-balloon {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          height: 0 !important;
          width: 0 !important;
          padding: 0 !important;
          margin: 0 !important;
          border: none !important;
        }

        /* Scoped CKEditor styling overrides for seamless composer integration */
        .ticket-reply-editor-wrapper {
          position: relative;
          width: 100%;
        }
        
        .ticket-reply-editor-wrapper .ck.ck-editor {
          border: none !important;
          box-shadow: none !important;
          display: flex;
          flex-direction: column;
        }

        .ticket-reply-editor-wrapper .ck.ck-toolbar {
          background: ${isPrivate ? "rgba(255, 197, 61, 0.08)" : token.colorBgLayout} !important;
          border: none !important;
          border-bottom: 1px solid ${token.colorBorderSecondary} !important;
          border-radius: 0 !important;
          padding: 4px 8px !important;
        }

        .ticket-reply-editor-wrapper.hide-editor-toolbar .ck.ck-editor__top {
          display: none !important;
        }

        .ticket-reply-editor-wrapper .ck-editor__editable {
          min-height: 80px !important;
          max-height: 250px !important;
          overflow-y: auto !important;
          border: none !important;
          box-shadow: none !important;
          padding: 8px 12px !important;
          background: transparent !important;
          font-size: 13px !important;
          line-height: 1.5 !important;
          color: ${token.colorText} !important;
        }

        .ticket-reply-editor-wrapper .ck-editor__editable.ck-focused {
          border: none !important;
          box-shadow: none !important;
          outline: none !important;
        }

        /* Customizing toolbar buttons */
        .ticket-reply-editor-wrapper .ck.ck-button {
          border-radius: 4px !important;
          cursor: pointer !important;
          color: ${token.colorTextSecondary} !important;
        }

        .ticket-reply-editor-wrapper .ck.ck-button:hover {
          background: ${token.colorFillTertiary} !important;
          color: ${token.colorText} !important;
        }

        .ticket-reply-editor-wrapper .ck.ck-button.ck-on {
          background: ${isPrivate ? "rgba(217, 119, 6, 0.12)" : token.colorPrimaryBg} !important;
          color: ${isPrivate ? token.colorWarningActive : token.colorPrimary} !important;
        }

        /* Dark mode compatibility rules */
        body.dark-theme .ticket-reply-editor-wrapper .ck.ck-toolbar {
          background: ${isPrivate ? "#231f18" : "#181818"} !important;
          border-bottom-color: #1e2430 !important;
        }

        body.dark-theme .ticket-reply-editor-wrapper .ck-editor__editable {
          color: #E9EEF9 !important;
        }

        body.dark-theme .ticket-reply-editor-wrapper .ck.ck-button {
          color: #9CA3AF !important;
        }

        body.dark-theme .ticket-reply-editor-wrapper .ck.ck-button:hover {
          background: #242b3b !important;
          color: #ffffff !important;
        }

        body.dark-theme .ticket-reply-editor-wrapper .ck.ck-button.ck-on {
          background: ${isPrivate ? "#3d2e16" : "rgba(3, 84, 215, 0.2)"} !important;
          color: ${isPrivate ? "#faad14" : "#3b82f6"} !important;
        }

        body.dark-theme .ticket-reply-editor-wrapper .ck.ck-dropdown__panel {
          background: #181818 !important;
          border-color: #1e2430 !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4) !important;
        }

        body.dark-theme .ticket-reply-editor-wrapper .ck.ck-list__item .ck-button:hover {
          background: #242b3b !important;
          color: #ffffff !important;
        }

        body.dark-theme .ticket-reply-editor-wrapper .ck.ck-list__item .ck-button.ck-on {
          background: ${isPrivate ? "#3d2e16" : "rgba(3, 84, 215, 0.2)"} !important;
          color: ${isPrivate ? "#faad14" : "#3b82f6"} !important;
        }
      `}</style>

      {/* Styled Chat Editor prompt box */}
      <div
        style={{
          background: composerBg,
          borderRadius: 8,
          border: isFocused
            ? `1px solid ${activeColor}`
            : `1px solid ${token.colorBorderSecondary}`,
          boxShadow: isFocused ? activeOutline : `0 1px 2px rgba(0,0,0,0.01)`,
          display: "flex",
          flexDirection: "column",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
        }}
      >
        {/* Editor Area */}
        <div className={`ticket-reply-editor-wrapper ${!showToolbar ? "hide-editor-toolbar" : ""}`}>
          <CKEditor
            editor={ClassicEditor as any}
            config={{
              placeholder: getPlaceholder(isPrivate),
              extraPlugins: [CustomMentionPlugin],
            }}
            onReady={(editor: any) => {
              setEditorInstance(editor);

              editor.editing.view.document.on("keydown", (evt: any, data: any) => {
                const mState = mentionStateRef.current;
                
                if (mState.active) {
                  const filteredOptions = mentionOptionsRef.current.filter((user) => 
                    user.label.toLowerCase().includes(mState.query.toLowerCase())
                  );
                  
                  if (data.domEvent.code === 'ArrowDown') {
                    data.preventDefault();
                    evt.stop();
                    setMentionState(prev => ({ ...prev, selectedIndex: Math.min(prev.selectedIndex + 1, filteredOptions.length - 1) }));
                    return;
                  }
                  if (data.domEvent.code === 'ArrowUp') {
                    data.preventDefault();
                    evt.stop();
                    setMentionState(prev => ({ ...prev, selectedIndex: Math.max(prev.selectedIndex - 1, 0) }));
                    return;
                  }
                  if (data.domEvent.code === 'Enter' || data.domEvent.code === 'Tab') {
                    data.preventDefault();
                    evt.stop();
                    if (filteredOptions[mState.selectedIndex]) {
                      handleInsertMentionRef.current?.(filteredOptions[mState.selectedIndex]);
                    }
                    return;
                  }
                  if (data.domEvent.code === 'Escape') {
                    data.preventDefault();
                    evt.stop();
                    setMentionState(prev => ({ ...prev, active: false }));
                    return;
                  }
                }

                const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
                const isSubmitKey = data.domEvent.code === "Enter" || data.domEvent.code === "NumpadEnter";
                const hasModifier = isMac ? data.domEvent.metaKey : data.domEvent.ctrlKey;

                if (isSubmitKey && hasModifier) {
                  data.preventDefault();
                  evt.stop();
                  document.getElementById("ticket-reply-send-btn")?.click();
                }
              }, { priority: 'highest' });
            }}
            onChange={(event: any, editor: any) => {
              onChangeReply(editor.getData());
              
              try {
                const model = editor.model;
                const selection = model.document.selection;
                const position = selection.getFirstPosition();
                
                if (position && position.parent) {
                  const rangeBefore = model.createRange(model.createPositionAt(position.parent, 0), position);
                  let textBeforeCursor = "";
                  for (const item of rangeBefore.getItems()) {
                    if (item.is('$textProxy') || item.is('textProxy')) {
                      textBeforeCursor += item.data;
                    }
                  }
                  
                  const match = textBeforeCursor.match(/(?:^|\s)(@\w*)$/);
                  if (match) {
                    const query = match[1].substring(1);
                    
                    const viewSelection = editor.editing.view.document.selection;
                    const viewPosition = viewSelection.getFirstPosition();
                    const domRange = editor.editing.view.domConverter.viewRangeToDom(
                      editor.editing.view.createRange(viewPosition)
                    );
                    const rect = domRange.getBoundingClientRect();
                    const wrapperElement = editor.ui.view.element.parentElement || editor.ui.view.element;
                    const wrapperRect = wrapperElement.getBoundingClientRect();
                    
                    setMentionState(prev => ({
                      active: true,
                      query,
                      position: {
                        bottom: wrapperRect.bottom - rect.top + 5,
                        left: rect.left - wrapperRect.left,
                      },
                      selectedIndex: (prev.active && prev.query === query) ? prev.selectedIndex : 0,
                    }));
                  } else {
                    setMentionState((prev) => (prev.active ? { ...prev, active: false } : prev));
                  }
                }
              } catch (e) {
                // Safely catch any internal editor errors during DOM synchronization
                setMentionState((prev) => (prev.active ? { ...prev, active: false } : prev));
              }
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              // Delay hide to allow clicks on mention options
              setTimeout(() => setIsFocused(false), 200);
            }}
          />
          {mentionState.active && (
            <div
              style={{
                position: "absolute",
                bottom: mentionState.position.bottom,
                left: mentionState.position.left,
                background: token.colorBgElevated,
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                borderRadius: 12,
                zIndex: 1000,
                maxHeight: 250,
                overflowY: "auto",
                minWidth: 220,
                padding: 6,
                border: `1px solid ${token.colorBorderSecondary}`,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {mentionOptions
                .filter((user) => user.label.toLowerCase().includes(mentionState.query.toLowerCase()))
                .map((user, index) => (
                  <div
                    key={user.value}
                    onClick={() => handleInsertMention(user)}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      borderRadius: 8,
                      fontSize: 14,
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      background: index === mentionState.selectedIndex ? token.colorFillAlter : "transparent",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={() => setMentionState(prev => ({ ...prev, selectedIndex: index }))}
                  >
                    <Avatar size={24} style={{ backgroundColor: token.colorPrimary, fontSize: 12 }}>
                      {user.label.charAt(0).toUpperCase()}
                    </Avatar>
                    <span style={{ fontWeight: 500 }}>{user.label}</span>
                  </div>
                ))}
              {mentionOptions.filter((user) => user.label.toLowerCase().includes(mentionState.query.toLowerCase())).length === 0 && (
                <div style={{ padding: "8px 12px", color: token.colorTextSecondary, fontSize: 13, textAlign: "center" }}>No users found</div>
              )}
            </div>
          )}
        </div>

        {/* Footer controls strip */}
        <div
          className="reply-footer"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
            padding: "6px 12px",
            background: token.colorFillAlter,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            transition: "background-color 0.2s ease",
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
          }}
        >
          {/* Left Controls: Select Source Segmented Pill & Format Toggle */}
          <div
            className="reply-footer-left"
            style={{ display: "flex", alignItems: "center", gap: 12 }}
          >
            <ConfigProvider
              theme={{
                components: {
                  Segmented: {
                    itemSelectedBg: isPrivate
                      ? (token.colorWarningBg || "rgba(255, 197, 61, 0.15)")
                      : (token.colorPrimaryBg || "rgba(24, 144, 255, 0.1)"),
                    itemSelectedColor: isPrivate
                      ? (token.colorWarningActive || "#d48806")
                      : (token.colorPrimaryActive || "#096dd9"),
                    itemActiveBg: isPrivate
                      ? "rgba(255, 197, 61, 0.2)"
                      : "rgba(24, 144, 255, 0.15)",
                  },
                },
              }}
            >
              <Segmented
                size="small"
                value={isPrivate ? "private" : "public"}
                onChange={(value) => onChangePrivate(value === "private")}
                disabled={submitting}
                options={[
                  {
                    label: (
                      <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600 }}>
                        <MessageOutlined style={{ fontSize: 12 }} />
                        <span>Reply</span>
                      </span>
                    ),
                    value: "public",
                  },
                  {
                    label: (
                      <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600 }}>
                        <FileTextOutlined style={{ fontSize: 12 }} />
                        <span>Add note</span>
                        <Tooltip title="Write a private note for agents only">
                          <InfoCircleOutlined style={{ fontSize: 11, marginLeft: 2 }} />
                        </Tooltip>
                      </span>
                    ),
                    value: "private",
                  },
                ]}
              />
            </ConfigProvider>

            {isLinked && (
              <Checkbox
                checked={updateLinkedTickets}
                onChange={(e) => setUpdateLinkedTickets(e.target.checked)}
                style={{ fontSize: 11 }}
              >
                Send to all linked tickets
              </Checkbox>
            )}
          </div>

          {/* Right Controls: Send Button, Attachment Button, Format Button & Cancel Button */}
          <Space size="small">
            {/* Attachment Button */}
            <Upload
              beforeUpload={handleBeforeUpload}
              showUploadList={false}
              multiple
              disabled={submitting}
            >
              <Tooltip title="Add attachment">
                <Button
                  type="text"
                  size="small"
                  icon={<PaperClipOutlined style={{ fontSize: 13 }} />}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: fileList.length > 0 ? activeColor : token.colorTextSecondary,
                    borderRadius: 4,
                  }}
                />
              </Tooltip>
            </Upload>

            {/* Formatting tools toggle button */}
            <Tooltip title={showToolbar ? "Hide formatting tools" : "Show formatting tools"}>
              <Button
                type={showToolbar ? "default" : "text"}
                size="small"
                onClick={() => {
                  setShowToolbar(!showToolbar);
                  if (editorInstance) {
                    // Keep focus on editor when toggling toolbar
                    editorInstance.editing.view.focus();
                  }
                }}
                icon={<FontColorsOutlined style={{ fontSize: 12 }} />}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: showToolbar ? activeColor : token.colorTextSecondary,
                  borderRadius: 4,
                  border: showToolbar ? `1px solid ${activeColor}` : "none",
                  background: showToolbar
                    ? isPrivate
                      ? "rgba(255, 197, 61, 0.05)"
                      : "rgba(24, 144, 255, 0.05)"
                    : "transparent",
                }}
              />
            </Tooltip>

            {/* Cancel Button */}
            <Tooltip
              title="Cancel and clear typed content"
              mouseEnterDelay={0.4}
            >
              <Button
                id="ticket-reply-cancel-btn"
                size="small"
                onClick={handleCancel}
                disabled={submitting || !hasContent}
                style={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: 6,
                  fontWeight: 500,
                }}
              >
                Cancel
              </Button>
            </Tooltip>

            <ConfigProvider
              theme={{
                components: {
                  Button: {
                    colorPrimary: isPrivate
                      ? token.colorWarning
                      : token.colorPrimary,
                    colorPrimaryHover: isPrivate
                      ? token.colorWarningHover
                      : token.colorPrimaryHover,
                    colorPrimaryActive: isPrivate
                      ? token.colorWarningActive
                      : token.colorPrimaryActive,
                  },
                },
              }}
            >
              <Tooltip
                title={isPrivate ? "Send Note (Ctrl+Enter)" : "Send Reply (Ctrl+Enter)"}
                mouseEnterDelay={0.4}
              >
                <Button
                  id="ticket-reply-send-btn"
                  type="primary"
                  className="send-icon-btn"
                  size="small"
                  icon={
                    isPrivate ? (
                      <LockOutlined style={{ fontSize: 11 }} />
                    ) : (
                      <SendOutlined
                        style={{ transform: "rotate(-45deg)", fontSize: 10 }}
                      />
                    )
                  }
                  loading={submitting}
                  onClick={async () => {
                    const result = await onSendReply(updateLinkedTickets, fileList);
                    if (result) {
                      editorInstance?.setData(""); // Clear the editor only if the reply was successfully sent
                      setFileList([]);
                    }
                  }}
                  disabled={submitting || !hasContent}
                  style={{
                    boxShadow: `0 1px 2px rgba(0, 0, 0, 0.05)`,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontWeight: 600,
                    borderRadius: 6,
                  }}
                >
                  Send
                </Button>
              </Tooltip>
            </ConfigProvider>
          </Space>
        </div>

        {/* Selected Attachments list display */}
        {fileList.length > 0 && (
          <div
            style={{
              padding: "6px 12px",
              background: token.colorBgLayout,
              borderTop: `1px dashed ${token.colorBorderSecondary}`,
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
            }}
          >
            {fileList.map((item, idx) => (
              <Tag
                key={idx}
                closable
                onClose={() => handleRemoveFile(idx)}
                style={{ fontSize: 11, borderRadius: 4, display: "inline-flex", alignItems: "center" }}
              >
                <PaperClipOutlined style={{ marginRight: 4, fontSize: 10 }} />
                {item.name}
              </Tag>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
